import { Document, Image, Page, Text, View } from '@react-pdf/renderer'
import React from 'react'
import { Html } from 'react-pdf-html'
import { PdfBadge, tw } from '~/components/pdfs'
import { getListingTypeLabel } from '~/models'
import {
  STATUSES,
  cn,
  fmtAddress,
  getMaxSubstringLength,
  getPropertyTypeClassName,
  transformImageUrl,
} from '~/utils'
import {
  PdfBrokerFooter,
  PdfLeaseSpaces,
  PdfListingBrokers,
  PdfListingMeta,
  PdfTransactions,
} from './components'
import {
  PdfDisclaimersPage,
  ReportPdfImagesPage,
  ReportPdfMapPage,
  ReportPdfParcelPage,
  ReportPdfTitlePage,
} from './pages'
import type { ReportPdfSwitchProps } from './ReportPdfSwitch'

function stripBoldItalicTags(html: string): string {
  return html
    .replace(/<\/?b>/gi, '')
    .replace(/<\/?strong>/gi, '')
    .replace(/<\/?i>/gi, '')
    .replace(/<\/?em>/gi, '')
}

function getTruncatedDescription(
  description: string,
  type: string,
  status: string
) {
  const threshold = type === 'sale' ? (status === 'sold' ? 1750 : 2000) : 1000
  if (description.length > threshold) {
    return `${description.slice(0, threshold)}...`
  } else if (description.split('<p').length > 14) {
    return `${description.slice(0, 400)}...`
  }
  return description
}

function wrapLinksInAnchors(text: string) {
  // Match http(s)://... or www. ...
  // Ignore if already inside an <a ...>...</a>
  const urlRegex = /(?<!href=")(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi

  return text.replace(urlRegex, (match) => {
    // If it's already wrapped in <a ...>, leave it
    if (/<a[^>]*>[^<]*$/.test(text.slice(0, text.indexOf(match)))) {
      return match
    }
    const href = match.startsWith('www.') ? `https://${match}` : match
    const domain = href.match(/^(?:https?:\/\/)?(?:www\.)?([^\/]+)/i)?.[1]
    return `<a href="${href}">${domain}</a>`
  })
}

export default function ReportFlyersPdf({
  organization,
  title,
  filters,
  user,
  company,
  listings,
  parcels,
  pdfOptions,
  brokers,
}: ReportPdfSwitchProps) {
  return (
    <Document title={title}>
      {pdfOptions.showTitlePage && (
        <ReportPdfTitlePage
          title={title}
          company={company}
          user={user}
          brokers={brokers}
          listings={listings}
          pdfOptions={pdfOptions}
          orientation="portrait"
        />
      )}
      {pdfOptions.showMapPage && (
        <ReportPdfMapPage
          listings={listings}
          pdfOptions={pdfOptions}
          orientation="portrait"
        />
      )}
      {listings.map((listing, index) => {
        const name = listing.name.substring(
          0,
          getMaxSubstringLength(listing.name, 52)
        )
        return (
          <React.Fragment key={listing._id}>
            <Page
              id={listing.tid}
              size="LETTER"
              style={tw('px-8 pb-24 pt-8 font-sans')}>
              <View style={tw('font-sans')}>
                <View style={tw('flex flex-row items-start gap-4')}>
                  <PdfBadge
                    className={cn(
                      'mt-px text-white',
                      listing.type === 'sale' &&
                        'bg-[#0284c7] hover:bg-[#0284c7]',
                      listing.type === 'lease' &&
                        'bg-[#c026d3] hover:bg-[#c026d3]',
                      listing.type === 'saleLease' &&
                        'bg-[#4f46e5] hover:bg-[#4f46e5]'
                    )}>
                    {STATUSES[listing.status]}
                  </PdfBadge>
                  <Text style={tw('font-heading text-2xl leading-tight')}>
                    {name.length < listing.name.length ? `${name}...` : name}
                  </Text>
                </View>
                <Text style={tw('mt-1 font-sans text-lg')}>
                  {fmtAddress(listing.property, {
                    oneLine: true,
                    locationVisibility: listing.locationVisibility,
                  })}
                </Text>
              </View>
              <View style={tw('mt-2 flex flex-row gap-6')}>
                <View style={tw('w-7/12')}>
                  {(listing.images || []).length > 0 ? (
                    <Image
                      style={tw('h-[179px] w-[319px] rounded-sm')}
                      src={transformImageUrl(listing.images![0].thumbUrl)}
                    />
                  ) : (
                    <View style={tw('rounded-sm bg-gray-200 pb-[56.25%]')} />
                  )}

                  {(listing.meta.description || listing.meta.highlights) && (
                    <View style={tw('mt-4')}>
                      <Text
                        style={tw('font-heading mb-4 text-base leading-none')}>
                        Overview
                      </Text>
                      {listing.meta.description && (
                        <Html
                          style={tw('font-sans text-sm leading-tight')}
                          stylesheet={{
                            p: { margin: '0 0 10px' },
                            li: { margin: 0 },
                            ul: { margin: 0 },
                            'ul > ul': { margin: 0 },
                          }}>
                          {/* {wrapLinksInAnchors(
                            stripBoldItalicTags(listing.meta.description ?? '')
                          )} */}
                          {wrapLinksInAnchors(
                            getTruncatedDescription(
                              stripBoldItalicTags(
                                listing.meta.description ?? ''
                              ),
                              listing.type,
                              listing.status
                            )
                          )}
                        </Html>
                      )}
                      {listing.meta.highlights && (
                        <Html
                          style={tw('font-sans text-sm leading-tight')}
                          stylesheet={{
                            p: { margin: '0 0 10px' },
                            li: { margin: 0 },
                            ul: { margin: 0 },
                            'ul > ul': { margin: 0 },
                          }}>
                          {stripBoldItalicTags(listing.meta.highlights)}
                        </Html>
                      )}
                    </View>
                  )}

                  {(listing.transactions || []).length === 1 &&
                    listing.transactions![0].sale && (
                      <PdfTransactions listing={listing} filters={filters} />
                    )}

                  {!pdfOptions.showLeaseSpaces &&
                    (listing.leaseSpaces || []).filter((s) => s.available)
                      .length > 0 && (
                      <PdfLeaseSpaces filters={filters} listing={listing} />
                    )}
                  {listing.status === 'leased' &&
                    !pdfOptions.showLeaseTransactions &&
                    (listing.transactions || []).length > 0 && (
                      <PdfTransactions listing={listing} filters={filters} />
                    )}
                </View>

                <View style={tw('w-5/12')}>
                  <View style={tw('mb-2 flex flex-row items-start gap-4')}>
                    <Text style={tw('font-heading text-xl leading-none')}>
                      {getListingTypeLabel(listing)}
                    </Text>

                    {listing.isBusinessListing && (
                      <PdfBadge className="bg-[#a21caf] text-white hover:bg-[#a21caf]">
                        Business
                      </PdfBadge>
                    )}

                    {listing.propertyTypes.slice(0, 1).map((value, index) => {
                      return (
                        <PdfBadge
                          key={index}
                          className={cn(
                            'text-white',
                            getPropertyTypeClassName(value)
                          )}>
                          {value}
                        </PdfBadge>
                      )
                    })}
                  </View>
                  {(listing.propertySubtypes || []).length > 0 && (
                    <Text style={tw('mb-3 font-sans text-sm italic')}>
                      Secondary Uses:{' '}
                      {listing.propertySubtypes!.slice(0, 4).join(', ')}
                    </Text>
                  )}
                  <PdfListingMeta listing={listing} pdfOptions={pdfOptions} />
                  {pdfOptions.showBrokers && (
                    <PdfListingBrokers listing={listing} />
                  )}
                </View>
              </View>
              {listings.length > 1 && (
                <View fixed style={tw('absolute right-6 top-6')}>
                  <Text style={tw('font-sans text-sm italic')}>
                    Listing {index + 1} of {listings.length}
                  </Text>
                </View>
              )}
              <PdfBrokerFooter
                user={user}
                company={company}
                organization={organization}
                brokers={brokers}
              />
            </Page>

            {(pdfOptions.showLeaseSpaces || pdfOptions.showLeaseTransactions) &&
              listing.type !== 'sale' && (
                <Page size="LETTER" style={tw('p-8 font-sans')}>
                  {pdfOptions.showLeaseSpaces && (
                    <PdfLeaseSpaces listing={listing} filters={filters} />
                  )}
                  {pdfOptions.showLeaseTransactions && (
                    <PdfTransactions listing={listing} filters={filters} />
                  )}
                </Page>
              )}
            {pdfOptions.showImages && <ReportPdfImagesPage listing={listing} />}
            {pdfOptions.showParcels && (
              <ReportPdfParcelPage listing={listing} parcels={parcels} />
            )}
          </React.Fragment>
        )
      })}
      <PdfDisclaimersPage
        pdfOptions={pdfOptions}
        organization={organization}
        orientation="portrait"
      />
    </Document>
  )
}
